import React, { useMemo } from 'react';
import { useOnboarding } from '../../contexts/OnboardingContext';
import { useOnboardingInput } from '../../hooks/useOnboardingInput';
import { MdAttachFile, MdEast } from 'react-icons/md';
import { PiX } from 'react-icons/pi';
import TextareaIcon from '../../assets/images/brain_icon.png';

interface FooterProps {
  className?: string;
  uploaderPreviewClassName?: string;
  onSubmit: () => void;
}

const Footer = ({
  className,
  uploaderPreviewClassName = '',
  onSubmit,
}: FooterProps) => {
  const {
    questions,
    answer,
    selected,
    isAnswerUpdating,
    isLoading: _isLoading,
    isQuestionLoading,
    aiText,
    isEdit,
    setAnswer,
    setSelected,
    submitAnswer,
    updateAnswer,
    setIsAnswerUpdating,
  } = useOnboarding();

  const currentQuestion = useMemo(
    () => questions[questions.length - 1],
    [questions]
  );

  const {
    textareaRef,
    fileInputRef,
    files,
    errors,
    clearFiles,
    handleFileChange,
    openFileDialog,
    handleChange,
    validateAnswer,
    processResumeLinkedInAnswer,
    isResumeLinkedInQuestion,
    MIN_ROWS,
  } = useOnboardingInput({
    answer,
    setAnswer,
    isAnswerUpdating,
    currentQuestionCode: currentQuestion?.code,
  });

  const handleSubmitAnswer = async () => {
    if (isQuestionLoading) return;

    if (!validateAnswer(files.length)) {
      return;
    }

    // Handle resume and linkedin bio questions
    if (isResumeLinkedInQuestion) {
      const userResponse = await processResumeLinkedInAnswer();
      if (!userResponse) {
        setIsAnswerUpdating(false);
        return;
      }

      const payload = {
        question_id: currentQuestion?.id || '',
        user_response: JSON.stringify(userResponse),
        ai_text: aiText.find(item => item.question_id === currentQuestion?.id)?.ai_text || currentQuestion?.ai_text || '',
      };

      submitAnswer(payload);
    } else if (isEdit) {
      updateAnswer(
        currentQuestion?.id || '',
        answer,
        aiText.find(item => item.question_id === currentQuestion?.id)?.ai_text || currentQuestion?.ai_text || '',
        false,
        questions.length - 1,
        onSubmit,
        isEdit,
      );
    } else {
      submitAnswer({
        question_id: questions?.[questions.length - 1]?.id || '',
        user_response: answer,
        ai_text: aiText.find(item => item.question_id === currentQuestion?.id)?.ai_text || currentQuestion?.ai_text || '',
      });
    }

    onSubmit();
    setSelected('');
    setAnswer('');
    clearFiles();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmitAnswer();
    }
  };

  return (
    <>
      {errors.length > 0 && (
        <div className='mb-3'>
          {errors.map((error, index) => (
            <p key={index} className='text-sm text-red-500 mb-1'>
              {error}
            </p>
          ))}
        </div>
      )}
      {files.length > 0 && (
        <div
          className={`flex items-center justify-between gap-3 px-3 py-[0.875rem] border-2 border-gray-200 rounded-xl w-fit mb-3 ${uploaderPreviewClassName}`}
        >
          <span
            className={`font-heading text-lg font-medium ${files[0].name.split('.').pop() === 'pdf' ? 'text-red-500' : 'text-blue-500'}`}
          >
            {files[0].name.split('.').pop()?.toUpperCase()}
          </span>
          <span className='px-[0.8125rem] text-sm font-bold'>
            {files[0].name}
          </span>
          <PiX
            size={24}
            className='cursor-pointer text-red-500'
            onClick={clearFiles}
          />
        </div>
      )}
      <div
        className={`flex items-center justify-between gap-3 min-h-14 border-gray-200 border-2 rounded-lg px-3 py-2 w-full [&:focus-within]:border-primary-200 duration-200 ${className}`}
      >
        <img src={TextareaIcon} className='w-9 h-9 min-w-9 object-contain' />
        <textarea
          ref={textareaRef}
          className='w-full resize-none focus:outline-none'
          rows={MIN_ROWS}
          value={!selected ? answer : ''}
          onChange={!selected ? handleChange : undefined}
          onKeyDown={handleKeyDown}
          disabled={!!selected}
          placeholder={'What\'s on your mind?'}
        />
        <div className='flex self-end gap-2 mb-[2px]'>
          {isResumeLinkedInQuestion && (
            <button
              className='bg-primary-400 text-white rounded-md flex items-center justify-center w-8 h-8 focus:outline-none focus:ring-0 hover:bg-primary-500 duration-200 cursor-pointer disabled:bg-gray-400 disabled:hover:bg-gray-400'
              disabled={!!selected}
              onClick={openFileDialog}
            >
              <input
                multiple={false}
                type='file'
                className='hidden'
                ref={fileInputRef}
                onChange={handleFileChange}
                accept='.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
              />
              <MdAttachFile size={20} />
            </button>
          )}

          <button
            className='text-primary-400 flex items-center justify-center w-8 h-8 focus:outline-none focus:ring-0 cursor-pointer disabled:text-gray-400 disabled:hover:text-gray-400'
            disabled={(answer.length === 0 && files.length === 0) || !!selected || isQuestionLoading}
            onClick={handleSubmitAnswer}
          >
            <MdEast size={24} />
          </button>
        </div>
      </div>
    </>
  );
};

export default Footer;
